/**
 * useSiteContent — Session 213.78
 *
 * Public read-only hook for CMS content. Returns the merged page-key dict
 * (defaults + admin overrides). Cached in a module-level Promise so every
 * mounted component shares the same fetch.
 *
 * Session 213.79 — preview overlay. When the page is loaded with
 * ``?_cms_preview=<base64-json>`` in the URL, the encoded fields are
 * applied on top of the saved CMS values. This powers the "Preview"
 * button in CmsAdminTab without needing a server-side preview route.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

let _contentPromise = null;
let _contentCache = null;

function _fetch() {
  if (_contentPromise) return _contentPromise;
  _contentPromise = axios
    .get(`${API}/site-content`)
    .then((r) => {
      _contentCache = r.data?.content || {};
      return _contentCache;
    })
    .catch(() => {
      _contentCache = {};
      return _contentCache;
    });
  return _contentPromise;
}

/** Decode the optional `?_cms_preview=...` query-param overlay.
 *
 * Symmetric with ``buildPreviewUrl``: the payload is base64-encoded
 * UTF-8 bytes (NOT Latin1) so we decode through ``TextDecoder``.
 */
function _readPreviewOverlay() {
  if (typeof window === 'undefined') return {};
  try {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get('_cms_preview');
    if (!raw) return {};
    const bin = atob(decodeURIComponent(raw));
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    const json = new TextDecoder().decode(bytes);
    const parsed = JSON.parse(json);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

/**
 * Returns the field map for a given page key.
 * @param {string} key e.g. "footer", "homepage", "ntoa_landing"
 * @param {object} fallback Static defaults used if CMS fetch fails or
 *                          a particular field hasn't been overridden.
 */
export function useSiteContent(key, fallback = {}) {
  const previewOverlay = _readPreviewOverlay()[key] || {};
  const [fields, setFields] = useState(() => ({
    ...fallback,
    ...((_contentCache || {})[key] || {}),
    ...previewOverlay,
  }));

  useEffect(() => {
    let active = true;
    _fetch().then((content) => {
      if (!active) return;
      setFields({ ...fallback, ...(content[key] || {}), ..._readPreviewOverlay()[key] || {} });
    });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return fields;
}

/** Force a re-fetch — useful after a CMS save in admin context. */
export function refreshSiteContent() {
  _contentPromise = null;
  _contentCache = null;
}

/**
 * Build a preview URL for a public page with unsaved CMS edits applied.
 * Used by CmsAdminTab's Preview button.
 *
 * Encoding note: ``btoa()`` only handles Latin1, so any em-dash, smart
 * quote, ®, ™, accent, or emoji typed into a CMS field would otherwise
 * throw ``InvalidCharacterError``. We round-trip through ``TextEncoder``
 * to base64 the *UTF-8 bytes*, which the ``atob()``-side decodes
 * symmetrically (see ``_readPreviewOverlay`` below).
 *
 * @param {string} publicPath e.g. "/", "/ntoa", "/login"
 * @param {string} key        CMS page key e.g. "homepage"
 * @param {object} fields     The in-flight (unsaved) edits to overlay
 */
export function buildPreviewUrl(publicPath, key, fields) {
  if (typeof window === 'undefined') return publicPath;
  const overlay = { [key]: fields };
  const json = JSON.stringify(overlay);
  // UTF-8 → base64 (handles em-dashes, smart quotes, ®, ™, emoji, etc.)
  const bytes = new TextEncoder().encode(json);
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  const encoded = encodeURIComponent(btoa(bin));
  const sep = publicPath.includes('?') ? '&' : '?';
  return `${publicPath}${sep}_cms_preview=${encoded}`;
}
